import { BottomSheet, Text, Title, type UseModalState } from "@clipboard-health/ui-components";
import { DialogContent, Stack } from "@mui/material";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { usePatchComment } from "@src/appV2/redesign/Workplace/Review/api/usePatchComment";
import { type WorkplaceCommentType } from "@src/appV2/redesign/Workplace/Review/api/useWorkplaceComments";
import { useCallback } from "react";

interface ConfirmAnonymizeCommentBottomSheetProps {
  modalState: UseModalState;
  commentId: string;
  workplaceId: string;
  onAnonymized: (comment: WorkplaceCommentType) => void;
}

export function ConfirmAnonymizeCommentBottomSheet(props: ConfirmAnonymizeCommentBottomSheetProps) {
  const { modalState, commentId, workplaceId, onAnonymized } = props;
  const { showSuccessToast, showErrorToast } = useToast();

  const { mutate: patchComment, isLoading: commentIsUpdating } = usePatchComment(
    { workplaceId, commentId },
    {
      onSuccess: (data) => {
        onAnonymized(data.data);
        showSuccessToast("Your review is now anonymous");
        modalState.closeModal();
      },
      onError: () => {
        showErrorToast("Something went wrong while updating the review");
      },
    }
  );

  const handleClose = useCallback(() => {
    modalState.closeModal();
  }, [modalState]);

  return (
    <BottomSheet
      footer={
        <DialogFooter orientation="vertical">
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            isLoading={commentIsUpdating}
            onClick={async () => {
              patchComment({ anonymous: true });
            }}
          >
            Confirm
          </LoadingButton>
        </DialogFooter>
      }
      header={<BottomSheetHeader onClose={handleClose} />}
      modalState={modalState}
    >
      <DialogContent sx={{ backgroundColor: "unset", py: 8, mt: 8 }}>
        <Stack direction="column" alignItems="center" spacing={9}>
          <Title variant="h2" component="h2" sx={{ textAlign: "center", textWrap: "balance" }}>
            Change your review to anonymous?
          </Title>

          <Text>
            This will remove your name and any other personal information from the review.
          </Text>
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
